import { DecimalPipe } from '@angular/common';
import { Component, OnInit, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DocumentChunk } from '@reflact/docsearch';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { DividerModule } from 'primeng/divider';
import { DocSearchChunkDisplayConfig, DEFAULT_CHUNK_DISPLAY_CONFIG } from '@shared/index';
import { BotService } from 'src/app/shared/services/bot.service';
import { FileUploadService } from 'src/app/shared/services/file-upload.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { FunctionNameSuggestComponent } from '../../../../../../../shared/components/ai-bot/utils/function-name-suggest/function-name-suggest.component';
import { HintComponent } from "@shared-angular/components/hint/hint.component";
import { SkillIdInputComponent } from '../../../../../../../shared/components/ai-bot/utils/skill-id-input/skill-id-input.component';
import { SearchTestService } from '../search-test.service';
@Component({
  selector: 'app-documents-tab-detail',
  imports: [DecimalPipe,
    FormsModule,
    FunctionNameSuggestComponent,
    SkillIdInputComponent,
    HintComponent,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    MessageModule,
    TableModule,
    ToggleSwitchModule,
    AccordionModule,
    CardModule,
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    DividerModule
  ],
  providers: [DecimalPipe],
  templateUrl: './documents-tab-detail.component.html',
  styleUrl: './documents-tab-detail.component.scss'
})
export class DocumentsTabDetailComponent implements OnInit {
  public readonly selectedDocumentId = input<string>('');
  public readonly closeDrawer = output();

  constructor(
    private fileUploadService: FileUploadService,
    private toastr: ToastrService,
    public rss: RouteShareService,
    public searchTestService: SearchTestService,
    private botService: BotService
  ) { }

  public ngOnInit(): void {
    this.searchTestService.resetSearchTest();
  }

  protected documentFileName(name: string): string {
    const filenameArr = name.split('.');
    if (filenameArr.length === 2) return name.trim();
    return filenameArr.slice(0, filenameArr.length - 1).join('.').trim();
  }

  protected async saveDocument(genData: boolean = false) {
    const id = this.rss.document?._id ?? '';
    const doc = this.rss.documents.find(doc => doc._id === id);
    if (!doc) return;
    if (genData) {
      doc.functionName = await this.botService.suggestName(this.rss.botConfig._id, doc.description);
      if (doc.skillId == "") {
        doc.skillId = await this.botService.suggestSkill(this.rss.botConfig._id, doc.description, doc.filename);
      }
    }
    if (doc.skillId === '' && doc.functionName.trim() !== '') {
      doc.skillId = this.deriveSkillId(doc.functionName);
    }
    const result = await this.fileUploadService.changeFileProperties(
      id,
      doc.enabledForKnowledge,
      doc.skillId,
      doc.functionName.trim(),
      doc.description,
      this.rss.botConfig._id,
      false,
      doc.publiclyReachable,
      doc.chunkDisplayConfig,
      this.safeSearchType,
      doc.docSearchMemoryEnabled,
      doc.docSearchMemoryLimit
    );
    if ('status' in result) {
      let msg = result.message;
      if (result.errorType === 'SKILL_ID_DUPLICATE') {
        msg = $localize`:@@error-skill-id-duplicate:Diese Skill-ID wird bereits von einem anderen Skill in diesem Bot verwendet. Wähle eine andere Skill-ID.`;
      } else if (result.errorType === 'FUNCTION_NAME_DUPLICATE') {
        msg = $localize`:@@error-function-name-duplicate:Dieser Funktionsname wird bereits von einem anderen Skill in diesem Bot verwendet. Wähle einen anderen Namen.`;
      }
      this.toastr.error("SAVED", msg);
      await this.fileUploadService.getFiles(this.rss.botConfig._id);
      this.rss.document = this.rss.documents.find(d => d._id === id);
      return;
    }
    this.toastr.success("SAVED");
  }

  public get safeSearchType(): "vector" | "text" | "combined" {
    if (!this.rss.document) return "vector";
    this.rss.document.searchType ??= "vector";
    return this.rss.document.searchType;
  }

  public set safeSearchType(value: "vector" | "text" | "combined") {
    if (this.rss.document) {
      this.rss.document.searchType = value;
    }
  }

  public get safeDocSearchMemoryLimit(): number {
    if (!this.rss.document) return 2;
    this.rss.document.docSearchMemoryLimit ??= 2;
    return this.rss.document.docSearchMemoryLimit;
  }

  public set safeDocSearchMemoryLimit(value: number) {
    if (this.rss.document) {
      this.rss.document.docSearchMemoryLimit = value;
    }
  }

  private deriveSkillId(from: string): string {
    return from.replace(/[^a-zA-Z0-9]/g, '').replace(/^\d+/, '').slice(0, 20);
  }

  public readonly searchTypeOptions: { label: string; value: string }[] = [
    { label: $localize`:@@search-type-vector:Vektor`, value: 'vector' },
    { label: $localize`:@@search-type-combined:Kombiniert`, value: 'combined' }
  ];

  public get safeChunkDisplayConfig(): DocSearchChunkDisplayConfig {
    if (!this.rss.document) return { ...DEFAULT_CHUNK_DISPLAY_CONFIG };
    this.rss.document.chunkDisplayConfig ??= { ...DEFAULT_CHUNK_DISPLAY_CONFIG };
    return this.rss.document.chunkDisplayConfig;
  }

  public get documentChunks(): DocumentChunk[] {
    return this.rss.documentChunks ?? [];
  }
}
